import { getPublicAssetPath } from './public-path';
import type { WeightBlob } from './types';

export interface WeightSet {
  values: Float64Array;
  rows: number;
  cols: number;
}


type NativeController = InstanceType<SMEModule['SMEController']>;

const BACKEND_WEIGHTS_URL = '/api/weights/latest';
const STATIC_WEIGHTS_URL = getPublicAssetPath('weights/default_weights.json');

function copyView(view: Float64Array): Float64Array {
  return Float64Array.from(view as Float64Array);
}

async function loadModule(): Promise<SMEModule> {
  if (typeof window === 'undefined' || typeof window.createSMEModule !== 'function') {
    throw new Error('createSMEModule is not available. Did you copy sme_module.js into public/?');
  }
  return window.createSMEModule();
}

async function fetchWeightPayload(url: string): Promise<WeightBlob> {
  const response = await fetch(url, { cache: 'no-cache' });
  if (!response.ok) {
    throw new Error(`Failed to fetch weights from ${url} (${response.status})`);
  }
  return (await response.json()) as WeightBlob;
}

function parseWeightShape(shape: [number, number] | undefined, fallbackLength: number): [number, number] {
  if (Array.isArray(shape) && shape.length === 2) {
    const rows = Number(shape[0]);
    const cols = Number(shape[1]);
    if (Number.isFinite(rows) && rows > 0 && Number.isFinite(cols) && cols > 0) {
      return [rows, cols];
    }
  }
  return [fallbackLength, 1];
}

function normaliseWeights(payload: WeightBlob): WeightSet {
  if (!Array.isArray(payload.weights)) {
    throw new Error('Malformed weights payload.');
  }
  const [rows, cols] = parseWeightShape(payload.shape, payload.weights.length);
  const expected = rows * cols;
  if (payload.weights.length !== expected) {
    console.warn('Weights payload length does not match declared shape. Proceeding anyway.');
  }
  return {
    values: Float64Array.from(payload.weights),
    rows,
    cols,
  };
}

export async function fetchWeights(preferredUrl = BACKEND_WEIGHTS_URL): Promise<WeightSet> {
  const candidates = preferredUrl === STATIC_WEIGHTS_URL
    ? [STATIC_WEIGHTS_URL]
    : [preferredUrl, STATIC_WEIGHTS_URL];
  const errors: string[] = [];
  for (const url of candidates) {
    try {
      const payload = await fetchWeightPayload(url);
      return normaliseWeights(payload);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error));
    }
  }
  throw new Error(`Failed to fetch SME weights. Attempts: ${errors.join(' | ')}`);
}

export class SMEControllerClient {
  private module?: SMEModule;
  private controller?: NativeController;

  static async create(weights: Float64Array): Promise<SMEControllerClient> {
    const client = new SMEControllerClient();
    await client.initialize(weights);
    return client;
  }

  async initialize(weights: Float64Array): Promise<void> {
    this.module = await loadModule();
    this.controller = new this.module.SMEController();
    this.controller.loadWeights(Array.from(weights));
  }

  ensureController(): NativeController {
    if (!this.controller) {
      throw new Error('SME controller has not been initialised.');
    }
    return this.controller;
  }

  reset(phase = 0): void {
    this.ensureController().reset(phase);
  }

  loadWeights(weights: Float64Array): void {
    this.ensureController().loadWeights(Array.from(weights));
  }

  step(clamp = true): Float64Array {
    return copyView(this.ensureController().step(clamp));
  }

  basisState(): Float64Array {
    return copyView(this.ensureController().basisState());
  }

  cpgState(): Float64Array {
    return copyView(this.ensureController().cpgState());
  }

  numOutputs(): number {
    return this.ensureController().numOutputs();
  }
}
